import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, Cell, LabelList, Pie, PieChart, ResponsiveContainer, XAxis, YAxis } from 'recharts'

import { AffiliateBrandOrderRow, AffiliateOrdersBreakdownRow } from '@/api/affiliate/types/Affiliate'
import { SimpleTable, TableColumn } from '@/components/SimpleTable'
import { Card, CardContent } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { WarehousePreview } from '@/components/WarehousePreview'

const ORDERS_BREAKDOWN_PAGE_SIZE = 10

type Props = {
  ordersBreakdown: AffiliateOrdersBreakdownRow[]
  ordersByBrand: AffiliateBrandOrderRow[]
}

type PieLabelProps = {
  cx?: number
  cy?: number
  midAngle?: number
  outerRadius?: number
  payload?: {
    brand: string
    value: number
    percentage: number
  }
}

/** Pixels from slice outer edge to the radial anchor point of each label block. */
const PIE_LABEL_GAP = 18
/** Vertical distance between the two label text lines. */
const PIE_LABEL_LINE_STEP = 14

const PIE_COLORS = ['#2EA8D7', '#C2D6E3', '#B79C5E', '#DAD8D5', '#F4D400', '#ECE8D3', '#BFD8E8']
const RADIAN = Math.PI / 180

export const AffiliateOrdersTab = ({ ordersBreakdown, ordersByBrand }: Props) => {
  const { t } = useTranslation()
  const [showPiePercent, setShowPiePrecent] = useState(false)
  const [showBarPercent, setShowBarPrecent] = useState(false)
  const totalOrders = useMemo(() => ordersByBrand.reduce((sum, row) => sum + row.value, 0), [ordersByBrand])

  const barRows = useMemo(() => {
    if (totalOrders === 0) return []
    return ordersByBrand.slice(0, 9).map(row => ({
      ...row,
      percentage: (row.value / totalOrders) * 100,
    }))
  }, [ordersByBrand, totalOrders])

  const pieRows = useMemo(() => {
    if (totalOrders === 0) return []
    return ordersByBrand.slice(0, 6).map(row => ({
      ...row,
      percentage: Math.round((row.value / totalOrders) * 100),
    }))
  }, [ordersByBrand, totalOrders])

  const hasBreakdown = ordersBreakdown.length > 0
  const hasBrandData = barRows.length > 0

  const breakdownColumns = useMemo<TableColumn<AffiliateOrdersBreakdownRow>[]>(
    () => [
      {
        key: 'country',
        header: t('affiliate.country'),
        align: 'start',
        headerClassName: 'grow',
        className: 'grow',
        render: row => <WarehousePreview warehouse={row.country} />,
      },
      {
        key: 'storeName',
        header: t('affiliate.storeName'),
        align: 'center',
        render: row => row.storeName,
      },
      {
        key: 'totalUses',
        header: t('affiliate.totalUses'),
        align: 'end',
        render: row => row.totalUses,
      },
    ],
    [t]
  )

  const formatActualValue = (value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}K`
    }
    return `${value}`
  }

  const formattedValue = (value: number) => {
    if (showBarPercent) {
      return `${Math.round((value / totalOrders) * 100)}%`
    }
    return formatActualValue(value)
  }

  const renderPieLabel = ({ cx = 0, cy = 0, midAngle = 0, outerRadius = 0, payload }: PieLabelProps) => {
    if (!payload) {
      return null
    }

    const angle = -midAngle * RADIAN
    const cos = Math.cos(angle)
    const sin = Math.sin(angle)
    const labelRadius = outerRadius + PIE_LABEL_GAP
    const lx = cx + labelRadius * cos
    const ly = cy + labelRadius * sin
    const labelValue = showPiePercent ? `${payload.percentage}%` : formatActualValue(payload.value)

    // Anchor labels horizontally based on quadrant; near the vertical axis center them.
    const textAnchor: 'start' | 'end' | 'middle' = Math.abs(cos) < 0.2 ? 'middle' : cos > 0 ? 'start' : 'end'

    // Anchor the two-line text block away from the pie based on the slice direction
    // so labels never extend back over the slices.
    let firstBaselineY: number
    if (sin < -0.2) {
      // Upper half: stack both lines above the radial anchor point.
      firstBaselineY = ly - PIE_LABEL_LINE_STEP
    } else if (sin > 0.2) {
      // Lower half: stack both lines below the radial anchor point.
      firstBaselineY = ly + 10
    } else {
      // Side labels: keep the block centered on the anchor.
      firstBaselineY = ly - 2
    }

    return (
      <text x={lx} y={firstBaselineY} textAnchor={textAnchor} fill='#16324F'>
        <tspan x={lx} dy='0' fontSize='14' fontWeight='500'>
          {labelValue}
        </tspan>
        <tspan x={lx} dy={PIE_LABEL_LINE_STEP} fontSize='14'>
          {payload.brand}
        </tspan>
      </text>
    )
  }

  return (
    <div className='space-y-4'>
      <Card className='bg-white py-4'>
        <CardContent className='space-y-4'>
          <div className='text-text-secondary font-medium'>{t('affiliate.ordersBreakdown')}</div>
          {hasBreakdown ? (
            <SimpleTable
              columns={breakdownColumns}
              data={ordersBreakdown}
              keyExtractor={(row, index) => row.id + index}
              pageSize={ORDERS_BREAKDOWN_PAGE_SIZE}
              containerClassName='h-[354px]'
              equalColumns
            />
          ) : (
            <div className='border-border-default text-text-secondary rounded-lg border bg-white py-6 text-center text-sm'>
              {t('affiliate.noOrdersToShow')}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className='bg-white py-4'>
        <CardContent className='space-y-4'>
          <div className='flex items-center justify-between'>
            <div className='text-text-secondary font-medium'>{t('affiliate.ordersByBrand')}</div>
            <div className='flex items-center gap-2'>
              <span className='text-text-secondary'>{t('affiliate.numbers')}</span>
              <Switch checked={showBarPercent} onCheckedChange={setShowBarPrecent} />
              <span className='text-text-secondary'>{t('affiliate.percentage')}</span>
            </div>
          </div>
          {hasBrandData ? (
            <div className='h-[260px]' dir='ltr'>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart data={barRows} layout='vertical' margin={{ top: 10, right: 30, bottom: 10, left: 60 }}>
                  <YAxis type='category' dataKey='brand' fontSize={14} axisLine={false} tickLine={false} width={90} />
                  <XAxis type='number' hide />
                  <Bar
                    isAnimationActive={false}
                    dataKey={showBarPercent ? 'percentage' : 'value'}
                    radius={[0, 8, 8, 0]}
                    fill='#D4F1FF'
                    barSize={20}
                  >
                    <LabelList
                      dataKey='value'
                      position='right'
                      formatter={(value: number) => formattedValue(value)}
                      fill='#16324F'
                      fontSize={14}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className='text-text-secondary py-10 text-center text-sm'>{t('affiliate.noOrdersToShow')}</div>
          )}
        </CardContent>
      </Card>

      <Card className='bg-white py-4'>
        <CardContent className='space-y-4'>
          <div className='flex items-center justify-between'>
            <div className='text-text-secondary font-medium'>{t('affiliate.ordersByBrand')}</div>
            <div className='flex items-center gap-2'>
              <span className='text-text-secondary'>{t('affiliate.numbers')}</span>
              <Switch checked={showPiePercent} onCheckedChange={setShowPiePrecent} />
              <span className='text-text-secondary'>{t('affiliate.percentage')}</span>
            </div>
          </div>
          {hasBrandData ? (
            <div className='h-[300px]' dir='ltr'>
              <ResponsiveContainer width='100%' height='100%'>
                <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                  <Pie
                    data={pieRows}
                    dataKey={showPiePercent ? 'percentage' : 'value'}
                    nameKey='brand'
                    innerRadius={62}
                    outerRadius={102}
                    paddingAngle={1}
                    labelLine={false}
                    label={renderPieLabel}
                    isAnimationActive={false}
                  >
                    {pieRows.map((entry, index) => (
                      <Cell key={entry.brand} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className='text-text-secondary py-10 text-center text-sm'>{t('affiliate.noOrdersToShow')}</div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
